import React, { useState } from "react";
import {
  Box, Paper, Typography, Chip, Button, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert,
} from "@mui/material";
import EngineeringRoundedIcon from "@mui/icons-material/EngineeringRounded";
import SwapHorizontalCircleRoundedIcon from "@mui/icons-material/SwapHorizontalCircleRounded";
import { postData, API_ENDPOINTS } from "../../services/apiService";

/**
 * PRC-001 — engine version cutover card.
 *
 * Drops into BillingSettings as a top card. Shows the live
 * `pricing_engine_version` value (1 = legacy, 2 = PRC-001) with a guarded
 * toggle that asks for explicit confirmation before flipping.
 *
 * On flip, the backend pricing-tier service cache is busted on next read so
 * propagation to live quotes is at most ~60s.
 */
export default function EngineVersionCard({ value, onSaved }) {
  const [confirmTo, setConfirmTo] = useState(null); // 1 | 2 | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const current = Number(value);
  const target = current === 2 ? 1 : 2;
  const isV2 = current === 2;

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await postData(API_ENDPOINTS.updateCharge, {
        key: "pricing_engine_version",
        value_numeric: confirmTo,
      });
      setConfirmTo(null);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: isV2 ? "#A7F3D0" : "#FCD34D",
          background: isV2
            ? "linear-gradient(135deg, #ECFDF5, #F0FDFA)"
            : "linear-gradient(135deg, #FFFBEB, #FEF3C7)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: "12px",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isV2
                ? "linear-gradient(135deg, #34D399, #059669)"
                : "linear-gradient(135deg, #FBBF24, #D97706)",
              color: "white",
              boxShadow: isV2
                ? "0 4px 14px rgba(5,150,105,0.30)"
                : "0 4px 14px rgba(217,119,6,0.30)",
            }}
          >
            <EngineeringRoundedIcon />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.25 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0F172A" }}>
                Pricing Engine Version
              </Typography>
              <Chip
                size="small"
                label={`v${current || 1}`}
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  fontSize: 11,
                  bgcolor: isV2 ? "#059669" : "#D97706",
                  color: "white",
                }}
              />
              <Chip
                size="small"
                label={isV2 ? "PRC-001 active" : "Legacy active"}
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  fontSize: 10,
                  color: isV2 ? "#065F46" : "#78350F",
                  borderColor: isV2 ? "#6EE7B7" : "#FCD34D",
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: "#475569", maxWidth: 720 }}>
              {isV2 ? (
                <>
                  All new customer quotes use the PRC-001 weight-tier × multiplier
                  + handling-charge formula. Switch back to v1 to roll back to the
                  legacy 3-tier hierarchy + demand multipliers.
                </>
              ) : (
                <>
                  Quotes still use the legacy 3-tier engine. Once your weight-tier
                  multipliers and handling charges are tuned, flip to v2 to activate
                  the PRC-001 pricing engine for all new bookings.
                </>
              )}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<SwapHorizontalCircleRoundedIcon />}
            onClick={() => setConfirmTo(target)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              minWidth: 200,
              background: isV2
                ? "linear-gradient(135deg, #FBBF24, #D97706)"
                : "linear-gradient(135deg, #34D399, #059669)",
              boxShadow: isV2
                ? "0 4px 12px rgba(217,119,6,0.30)"
                : "0 4px 12px rgba(5,150,105,0.30)",
              "&:hover": {
                background: isV2
                  ? "linear-gradient(135deg, #D97706, #B45309)"
                  : "linear-gradient(135deg, #059669, #047857)",
              },
            }}
          >
            Switch to v{target}
          </Button>
        </Stack>
      </Paper>

      <Dialog open={confirmTo != null} onClose={() => !saving && setConfirmTo(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Switch pricing engine to v{confirmTo}?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5, color: "#334155" }}>
            {confirmTo === 2 ? (
              <>This will route every new customer quote through the PRC-001 v2 engine — per-kg/per-km × weight-tier multiplier + handling charge.</>
            ) : (
              <>This will roll back to the legacy 3-tier engine (trip override → owner truck rate → platform tier) with demand multipliers re-active.</>
            )}
          </Typography>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Trips already in flight retain the engine version snapshotted at their creation. Only NEW bookings change.
          </Alert>
          {error && (
            <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmTo(null)} disabled={saving} sx={{ textTransform: "none", fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmTo === 2 ? "success" : "warning"}
            onClick={handleConfirm}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, px: 3 }}
          >
            {saving ? "Switching…" : `Confirm switch`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
